import { PayloadAction } from "@reduxjs/toolkit";
import { findAreaInWorkflow, getAreaQuantity, normalizeAreaFromBackend } from "../utils/normalize";
import { QuoteCreateState, SectorWorkflowState, AreaType } from "../types";
import { EMonetaryAmountBy } from "../../../../../components/invoice/create/interfaces/invoice.interfaces";
import { isCommercialCategory, isResidentialOnlySpecialRequest } from "../utils/commercialSpecialRequests";

export enum QuoteCreateAdvancedSectorWorkflowStepsEnum {
  Categories = "categories",
  TypeOfPlaces = "type-of-places",
  TypeOfService = "type-of-service",
  Areas = "areas",
  Objects = "objects",
  SpecialServices = "special-requests",
}

export const QUOTE_CREATE_ADVANCED_SECTORWORKFLOW_STEPS: QuoteCreateAdvancedSectorWorkflowStepsEnum[] = [
  QuoteCreateAdvancedSectorWorkflowStepsEnum.Categories,
  QuoteCreateAdvancedSectorWorkflowStepsEnum.TypeOfPlaces,
  QuoteCreateAdvancedSectorWorkflowStepsEnum.TypeOfService,
  QuoteCreateAdvancedSectorWorkflowStepsEnum.Areas,
  QuoteCreateAdvancedSectorWorkflowStepsEnum.Objects,
  QuoteCreateAdvancedSectorWorkflowStepsEnum.SpecialServices,
];

export type QuoteCreateSectorStep = QuoteCreateAdvancedSectorWorkflowStepsEnum;

export const getSectorWorkflowStepOrder = (sectorWorkflow: SectorWorkflowState): QuoteCreateAdvancedSectorWorkflowStepsEnum[] => {
  if (sectorWorkflow.specialRequestsStepEnabled === false) {
    return QUOTE_CREATE_ADVANCED_SECTORWORKFLOW_STEPS.filter((s) => s !== QuoteCreateAdvancedSectorWorkflowStepsEnum.SpecialServices);
  }
  return QUOTE_CREATE_ADVANCED_SECTORWORKFLOW_STEPS;
};

export const initialSectorWorkflow: SectorWorkflowState = {
  step: QuoteCreateAdvancedSectorWorkflowStepsEnum.Categories,
  isComplete: false,
  alias: "",
  squareft: null,
  averagePeople: null,
  specialRequestsStepEnabled: null,
  category: null,
  places: null,
  service: null,
  areas: [],
  otherAreas: [],
  objects: [],
  specialRequests: [],
  costOverride: null,
  serviceNameOverride: "",
  frequencyType: "weekly",
};

const sectorWorkflowReducers = {
  setSectorStep: (state: QuoteCreateState, action: PayloadAction<QuoteCreateSectorStep>) => {
    state.sectorWorkflow.step = action.payload;
  },
  setSectorWorkflowAlias: (state: QuoteCreateState, action: PayloadAction<string>) => {
    state.sectorWorkflow.alias = action.payload;
  },
  previousSectorStep: (state: QuoteCreateState) => {
    const order = getSectorWorkflowStepOrder(state.sectorWorkflow);
    const index = order.indexOf(state.sectorWorkflow.step as QuoteCreateSectorStep);

    if (index <= 0) {
      if (index === -1) {
        state.sectorWorkflow.step = order[0] ?? QuoteCreateAdvancedSectorWorkflowStepsEnum.Categories;
      }
      return;
    }

    const prev = order[index - 1];

    if (prev === QuoteCreateAdvancedSectorWorkflowStepsEnum.Categories) {
      const specialRequestsStepEnabled = state.sectorWorkflow.specialRequestsStepEnabled;
      state.sectorWorkflow = { ...initialSectorWorkflow, specialRequestsStepEnabled };
    } else {
      state.sectorWorkflow.step = prev;
    }
  },

  nextSectorStep: (state: QuoteCreateState) => {
    const order = getSectorWorkflowStepOrder(state.sectorWorkflow);
    const index = order.indexOf(state.sectorWorkflow.step as QuoteCreateSectorStep);

    if (index === -1) {
      state.sectorWorkflow.step = order[0] ?? QuoteCreateAdvancedSectorWorkflowStepsEnum.Categories;
      return;
    }

    if (index < order.length - 1) {
      state.sectorWorkflow.step = order[index + 1];
    } else {
      state.sectorWorkflow.isComplete = true;
    }
  },

  resetSectorStep: (state: QuoteCreateState) => {
    state.sectorWorkflow = { ...initialSectorWorkflow };
  },

  resetSectorWorkflow: (state: QuoteCreateState) => {
    state.sectorWorkflow = { ...initialSectorWorkflow };
  },

  setCategory: (state: QuoteCreateState, action: PayloadAction<any>) => {
    const prevUid = state.sectorWorkflow.category?.uid;
    const next = action.payload;
    state.sectorWorkflow.category = next;

    if (prevUid != null && next?.uid != null && prevUid !== next.uid) {
      state.sectorWorkflow.places = null;
      state.sectorWorkflow.service = null;
      state.sectorWorkflow.areas = [];
      state.sectorWorkflow.otherAreas = [];
      state.sectorWorkflow.objects = [];
      state.sectorWorkflow.squareft = null;
      state.sectorWorkflow.averagePeople = null;
      if (state.sectorWorkflow.step !== QuoteCreateAdvancedSectorWorkflowStepsEnum.Categories) {
        state.sectorWorkflow.step = QuoteCreateAdvancedSectorWorkflowStepsEnum.TypeOfPlaces;
      }
    }

    if (isCommercialCategory(next)) {
      state.sectorWorkflow.specialRequests = (state.sectorWorkflow.specialRequests ?? []).filter((r) => !isResidentialOnlySpecialRequest(r));
    }
  },

  setPlace: (state: QuoteCreateState, action: PayloadAction<any>) => {
    state.sectorWorkflow.places = action.payload;
    if (state.sectorWorkflow.averagePeople == null || state.sectorWorkflow.averagePeople < 1) {
      state.sectorWorkflow.averagePeople = 1;
    }
  },

  setSpecialRequestsStepEnabled: (state: QuoteCreateState, action: PayloadAction<boolean | null>) => {
    state.sectorWorkflow.specialRequestsStepEnabled = action.payload;
    if (action.payload === false && state.sectorWorkflow.step === QuoteCreateAdvancedSectorWorkflowStepsEnum.SpecialServices) {
      state.sectorWorkflow.step = QuoteCreateAdvancedSectorWorkflowStepsEnum.Objects;
    }
  },

  setService: (state: QuoteCreateState, action: PayloadAction<any>) => {
    state.sectorWorkflow.service = action.payload;
  },

  addSquareft: (state: QuoteCreateState, action: PayloadAction<number>) => {
    state.sectorWorkflow.squareft = action.payload;
  },

  addAveragePeople: (state: QuoteCreateState, action: PayloadAction<number>) => {
    state.sectorWorkflow.averagePeople = action.payload;
  },

  initializeAreasFromBackend: (state: QuoteCreateState, action: PayloadAction<AreaType[]>) => {
    state.sectorWorkflow.areas = action.payload.map((area) => normalizeAreaFromBackend(area));
  },

  setOtherAreas: (state: QuoteCreateState, action: PayloadAction<any[]>) => {
    state.sectorWorkflow.otherAreas = action.payload?.map((area) => normalizeAreaFromBackend(area));
  },

  updateTaskNote: (
    state,
    action: PayloadAction<{
      areaUid: string;
      taskId: string | number;
      note: string;
    }>,
  ) => {
    const { areaUid, taskId, note } = action.payload;

    const area = findAreaInWorkflow(state, areaUid);
    if (!area) return;

    const task = area.tasks.find((item) => {
      const currentTaskId = item?.uid ?? item?.id;

      return String(currentTaskId) === String(taskId);
    });

    if (task) {
      task.note = note;
    }
  },

  changeAreaQuantity: (
    state,
    action: PayloadAction<{
      areaUid: string;
      delta: number;
    }>,
  ) => {
    const { areaUid, delta } = action.payload;

    const area = findAreaInWorkflow(state, areaUid);
    if (!area) return;

    const currentQuantity = getAreaQuantity(area);
    const newQty = Math.max(0, currentQuantity + delta);

    if (area.quantity != null) {
      area.quantity = newQty;
    } else {
      if (!Array.isArray(area.squareFootages)) {
        area.squareFootages = [];
      }

      if (!area.squareFootages[0]) {
        area.squareFootages[0] = {
          quantity: 0,
        };
      }

      area.squareFootages[0].quantity = newQty;
    }

    if (newQty === 0 && Array.isArray(area.tasks)) {
      area.tasks.forEach((task) => {
        task.active = false;
      });
    }
  },

  toggleAreaTaskActive: (state, action: PayloadAction<{ areaUid: string; taskUid: string | number }>) => {
    const { areaUid, taskUid } = action.payload;

    const area = findAreaInWorkflow(state, areaUid);
    if (!area) return;

    const task = area.tasks.find((t) => t.id === taskUid || t.uid === taskUid);

    if (task) {
      task.active = !task.active;
    }
  },

  removeTask: (state, action) => {
    const { areaUid, taskId } = action.payload;

    const area = findAreaInWorkflow(state, areaUid);
    if (!area) return;

    area.tasks = area.tasks?.filter((t) => t.id !== taskId && t.uid !== taskId);
  },

  setServiceNameOverride: (state, action) => {
    state.sectorWorkflow.serviceNameOverride = action.payload;
  },

  updateFrequencyType: (state: QuoteCreateState, action: PayloadAction<string>) => {
    state.sectorWorkflow.frequencyType = action.payload;
  },

  // OTHER AREAS

  toggleOtherArea: (state, action: PayloadAction<any>) => {
    const rawArea = action.payload;

    const exists = state.sectorWorkflow.otherAreas.find((x) => x.uid === rawArea.uid);

    if (exists) {
      // Quitar
      state.sectorWorkflow.otherAreas = state.sectorWorkflow.otherAreas?.filter((x) => x.uid !== rawArea.uid);
    } else {
      // AGREGAR — PERO NORMALIZADO
      const normalized = normalizeAreaFromBackend(rawArea);
      state.sectorWorkflow.otherAreas.push(normalized);
    }
  },

  clearOtherAreas: (state) => {
    state.sectorWorkflow.otherAreas = [];
  },

  // OBJECTS

  toggleObject: (state: QuoteCreateState, action: PayloadAction<any>) => {
    const obj = action.payload;

    const exists = state.sectorWorkflow.objects.find((x) => x.uid === obj.uid);

    if (exists) {
      state.sectorWorkflow.objects = state.sectorWorkflow.objects?.filter((x) => x.uid !== obj.uid);
    } else {
      state.sectorWorkflow.objects.push(obj);
    }
  },

  clearSelectedObjects: (state) => {
    state.sectorWorkflow.objects = [];
  },

  // SPECIAL REQUESTS

  toggleSpecialRequest: (state: QuoteCreateState, action: PayloadAction<any>) => {
    const req = action.payload;

    if (isCommercialCategory(state.sectorWorkflow.category) && isResidentialOnlySpecialRequest(req)) {
      return;
    }

    const exists = state.sectorWorkflow.specialRequests.find((x) => x.uid === req.uid);

    if (exists) {
      state.sectorWorkflow.specialRequests = state.sectorWorkflow.specialRequests?.filter((x) => x.uid !== req.uid);
    } else {
      state.sectorWorkflow.specialRequests.push(req);
    }
  },

  clearSpecialRequests: (state) => {
    state.sectorWorkflow.specialRequests = [];
  },

  setCostOverride: (
    state: QuoteCreateState,
    action: PayloadAction<{
      by: EMonetaryAmountBy;
      value: number;
      currency?: string;
    }>,
  ) => {
    state.sectorWorkflow.costOverride = {
      by: action.payload.by,
      value: action.payload.value,
      currency: action.payload.currency ?? "USD",
    };
  },

  updateCostOverrideValue: (state: QuoteCreateState, action: PayloadAction<number>) => {
    if (!state.sectorWorkflow.costOverride) {
      state.sectorWorkflow.costOverride = {
        by: EMonetaryAmountBy.UNIQUE,
        value: action.payload,
        currency: "USD",
      };
      return;
    }

    state.sectorWorkflow.costOverride.value = action.payload;
  },

  updateCostOverrideBy: (state: QuoteCreateState, action: PayloadAction<EMonetaryAmountBy>) => {
    if (!state.sectorWorkflow.costOverride) {
      state.sectorWorkflow.costOverride = {
        by: action.payload,
        value: 0,
        currency: "USD",
      };
      return;
    }

    state.sectorWorkflow.costOverride.by = action.payload;
  },

  clearCostOverride: (state: QuoteCreateState) => {
    state.sectorWorkflow.costOverride = null;
  },
};

export default sectorWorkflowReducers;
